import React from "react";
import { Nav, Card, Button, ButtonIcon, Loader, Table, Thead, Tbody, Tr, Th, Td } from "ui-kit-ck-consultant";
import FadeIn from "react-fade-in";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

import AuthContext from "../../context/AuthContext";
import axiosConfig from "../../utils/axiosConfig";
import downloadFile from "../../utils/downloadFile";
import "./document-editor.css";

const key = (sheet, ref) => `${sheet}!${ref}`;

const formatSize = (bytes) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} Mo`
    : `${Math.max(1, Math.round(bytes / 1024))} Ko`;

const errorMessage = (err) =>
  (err.response && err.response.data && err.response.data.message) ||
  "Erreur lors de l'enregistrement";

const cellCss = (style) => ({
  fontWeight: style.bold ? "bold" : "normal",
  fontStyle: style.italic ? "italic" : "normal",
  fontSize: style.size ? `${style.size}pt` : undefined,
  color: style.color || undefined,
  backgroundColor: style.background || undefined,
  textAlign: style.align || undefined,
  verticalAlign: style.valign || undefined,
});

// The in-place editor grows with its content, so a long narrative (the audit
// paragraph spans ten columns and a few hundred words) is never cropped.
class CellInput extends React.Component {
  ref = React.createRef();

  componentDidMount() {
    this.fit();
    const el = this.ref.current;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }

  componentDidUpdate() {
    this.fit();
  }

  fit = () => {
    const el = this.ref.current;
    el.style.height = "0";
    el.style.height = `${el.scrollHeight}px`;
  };

  render() {
    return <textarea ref={this.ref} rows={1} {...this.props} />;
  }
}

// Edits cell values of a stored spreadsheet. The server renders the grid from the file
// and writes the values back into it, so the Excel formatting is never touched here.
export default class DocumentEditor extends React.Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      title: "",
      filename: "",
      version: null,
      editable: false,
      sheets: [],
      versions: [],
      changes: {},
      editing: null,
    };
  }

  componentDidMount() {
    this.load();
  }

  id = () => this.props.match.params.id;

  load = () => {
    this.setState({ isLoading: true, changes: {}, editing: null });
    axiosConfig(`documents/${this.id()}/grid`)
      .then((res) => {
        const d = res.data.data;
        this.setState({
          isLoading: false,
          title: d.title,
          filename: d.filename,
          version: d.version,
          editable: d.editable,
          sheets: d.sheets,
        });
      })
      .catch(() => this.setState({ isLoading: false }));
    axiosConfig(`documents/${this.id()}/versions`).then((res) => {
      this.setState({ versions: res.data.success ? res.data.data : [] });
    });
  };

  startEditing = (sheet, cell) => {
    if (!this.state.editable || !cell.editable) return;
    const pending = this.state.changes[key(sheet, cell.ref)];
    this.setState({
      editing: { sheet, ref: cell.ref, value: pending ? pending.value : cell.text },
    });
  };

  commitEditing = () => {
    const { editing } = this.state;
    if (!editing) return;
    this.setState((prev) => ({
      editing: null,
      changes: { ...prev.changes, [key(editing.sheet, editing.ref)]: editing },
    }));
  };

  handleKeyDown = (event) => {
    if (event.key === "Escape") this.setState({ editing: null });
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.commitEditing();
    }
  };

  save = () => {
    const changes = Object.values(this.state.changes).map(({ sheet, ref, value }) => ({
      sheet,
      ref,
      value,
    }));
    axiosConfig({
      url: `documents/${this.id()}/cells`,
      data: { version: this.state.version, changes },
    })
      .then((res) => {
        if (res.data.success) this.load();
        else alert(res.data.message || "Erreur lors de l'enregistrement");
      })
      .catch((err) => {
        alert(errorMessage(err));
        if (err.response && err.response.status === 409) this.load();
      });
  };

  download = (url = `documents/${this.id()}/download`) =>
    downloadFile(url).catch(() => alert("Téléchargement impossible"));

  renderCell(sheet, cell) {
    if (cell.hidden) return null;
    const { editing, changes } = this.state;
    const pending = changes[key(sheet.name, cell.ref)];
    const isEditing = editing && editing.sheet === sheet.name && editing.ref === cell.ref;
    const classes = [
      this.state.editable && cell.editable ? "editable" : "",
      cell.formula ? "formula" : "",
      pending ? "changed" : "",
      isEditing ? "editing" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <td
        key={cell.ref}
        colSpan={cell.colSpan || 1}
        rowSpan={cell.rowSpan || 1}
        className={classes || undefined}
        style={cellCss(cell.style)}
        title={cell.formula ? "Cellule calculée" : cell.ref}
        onClick={() => (isEditing ? null : this.startEditing(sheet.name, cell))}
      >
        {isEditing ? (
          <CellInput
            value={editing.value}
            onChange={(e) => this.setState({ editing: { ...editing, value: e.target.value } })}
            onBlur={this.commitEditing}
            onKeyDown={this.handleKeyDown}
          />
        ) : (
          (pending ? pending.value : cell.text)
        )}
      </td>
    );
  }

  renderSheet(sheet) {
    return (
      <Card key={sheet.name} title={sheet.name} className="mb-20">
        <div className="xlsx-wrap">
          <table className="xlsx">
            <colgroup>
              {sheet.colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <tbody>
              {sheet.rows.map((row, r) => (
                <tr key={r} style={row.height ? { height: `${row.height}px` } : undefined}>
                  {row.cells.map((cell) => this.renderCell(sheet, cell))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  renderVersions() {
    const { versions } = this.state;
    return (
      <Card title="Historique" className="mb-20">
        <Table empty={versions.length ? "" : "Aucune version antérieure"}>
          <Thead>
            <Tr>
              <Th>Version</Th>
              <Th>Enregistrée par</Th>
              <Th>Le</Th>
              <Th>Taille</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {versions.map((v) => (
              <Tr key={v.id}>
                <Td>v{v.version}</Td>
                <Td>{v.savedBy || "—"}</Td>
                <Td className="nowrap">{v.savedAt}</Td>
                <Td className="nowrap">{formatSize(v.size)}</Td>
                <Td className="nowrap">
                  <ButtonIcon
                    small
                    style={{ color: "var(--primary-color)" }}
                    onClick={() => this.download(`documents/${this.id()}/versions/${v.id}/download`)}
                  >
                    <FontAwesomeIcon icon={faDownload} title="Télécharger" />
                  </ButtonIcon>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    );
  }

  render() {
    const { sheets, editable, changes, version } = this.state;
    const dirty = Object.keys(changes).length > 0;

    return (
      <FadeIn>
        <Nav
          title={this.state.title || "Document"}
          subtitle={version ? `${this.state.filename} — v${version}` : ""}
          back
          onBack={() => this.props.history.push("/documents")}
          className="mb-20"
        >
          {editable ? (
            <Button text="Enregistrer" onClick={this.save} disabled={!dirty} className="mr-10" />
          ) : null}
          <Button text="Télécharger" onClick={() => this.download()} />
        </Nav>
        {editable ? (
          <p className="xlsx-legend">
            Cliquez sur une cellule pour la modifier. Les cellules en italique sont calculées
            et suivent leur source. Entrée valide, Maj+Entrée ajoute une ligne.
          </p>
        ) : null}
        {!this.state.isLoading && !sheets.length ? (
          <Card className="mb-20">Ce document ne s'affiche pas en ligne : téléchargez-le.</Card>
        ) : null}
        {sheets.map((sheet) => this.renderSheet(sheet))}
        {this.renderVersions()}
        <Loader show={this.state.isLoading} />
      </FadeIn>
    );
  }
}
